import type { PrecipIntensity } from '../types/forecast.js';

/**
 * NL-formatting + kleine reken-helpers voor de gedeelde plaats-view (§5).
 * Pure functies, geen DOM — bruikbaar in 11ty (build) én Fastify (SSR).
 * Waarden uit het `ForecastModel` zijn al metrisch; hier alleen weergave.
 */

/** HTML-escape voor tekst uit data (plaatsnamen e.d.) in de output. */
export function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Vult `{token}`-plaatsen in een vaste tekst (uit `text/ui-copy.ts`) met waarden — zo blijft de copy
 * bewerkbaar als hele zin met placeholders, terwijl de variabelen in de view-laag blijven. Géén
 * HTML-escaping (dit zijn plain-string-zinnen, geen HTML); voor HTML loopt rendering via `html``.
 */
export function fillText(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

/** Hele graden: "14°". */
export function formatTemp(t: number): string {
  return `${Math.round(t)}°`;
}

/** Neerslag in mm met NL-decimaalkomma: "4,2 mm". */
export function formatMM(mm: number): string {
  return `${mm.toFixed(1).replace('.', ',')} mm`;
}

const COMPASS = ['N', 'NO', 'O', 'ZO', 'Z', 'ZW', 'W', 'NW'] as const;

/** Windrichting in graden → 8-punts kompaslabel (richting waar de wind vandaan komt). */
export function degToCompass(deg: number): string {
  const i = Math.round((((deg % 360) + 360) % 360) / 45) % 8;
  return COMPASS[i] ?? 'N';
}

/** Wind-balk percentage (mock: schaal ~60 km/u). */
export function windPercent(kmh: number): number {
  return Math.min(100, Math.round((kmh / 60) * 100));
}

/**
 * Windkracht in Beaufort (0–12) uit km/u. Bovengrenzen per Bft; ≥118 km/u = 12.
 * bron: KNMI/WMO Beaufort-schaal (https://www.knmi.nl/kennis-en-datacentrum/uitleg/windschaal-van-beaufort).
 */
export function beaufort(kmh: number): number {
  const ceilings = [1, 6, 12, 20, 29, 39, 50, 62, 75, 89, 103, 118];
  const i = ceilings.findIndex((c) => kmh < c);
  return i < 0 ? 12 : i;
}

/** Vocht-sublabel (mock: >72 vochtig, <45 droog, anders behaaglijk). */
export function humidityLabel(humidity: number): string {
  return humidity > 72 ? 'vochtig' : humidity < 45 ? 'droog' : 'behaaglijk';
}

/**
 * Neerslag-categorie. De dag-schaal verschilt van de uur-schaal (mock `rainLevel`):
 * per uur <1 licht / <2,5 matig / ≥2,5 zwaar; per dag <2 licht / <8 matig / ≥8 zwaar.
 */
export function precipIntensity(mm: number, daily: boolean): PrecipIntensity {
  if (mm <= 0) return 'geen';
  if (daily) return mm < 2 ? 'licht' : mm < 8 ? 'matig' : 'zwaar';
  return mm < 1 ? 'licht' : mm < 2.5 ? 'matig' : 'zwaar';
}

/** Categorie-woord voor in de cel (geen → leeg). */
export function precipWord(intensity: PrecipIntensity): string {
  return intensity === 'geen' ? '' : intensity;
}

/** Temperatuur → tint (mock `tempHue`): koel-blauw … warm-rood. */
export function tempHue(t: number): string {
  if (t <= 0) return '#5b7fb0';
  if (t < 6) return '#5f8fb6';
  if (t < 12) return '#6f9a8e';
  if (t < 18) return '#8a8f6a';
  if (t < 24) return '#b07a45';
  return '#b65a3a';
}

/** "HH:MM" uit een lokaal-ISO-tijd ("2026-06-01T14:00" → "14:00"). Forecast-tijden zijn al Amsterdam. */
export function clock(localIso: string): string {
  return localIso.slice(11, 16);
}

// `fetchedAt` is UTC (toISOString); converteer expliciet naar Amsterdam (DST-proof).
const AMS_TIME = new Intl.DateTimeFormat('nl-NL', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/Amsterdam',
});

/**
 * "Bijgewerkt om HH:MM" uit een (UTC-)ISO-tijd. v1 toont de stale-status niet; v2 vervangt deze
 * regel bij stale data door een kalme melding (zie `WeatherViewModel.staleText`).
 */
export function formatUpdated(fetchedAtIso: string): string {
  return `Bijgewerkt om ${updatedClock(fetchedAtIso)}`;
}

/** Alleen de kloktijd (HH:MM, Amsterdam) van het ophaalmoment — voor de stale-melding (v2). */
export function updatedClock(fetchedAtIso: string): string {
  return AMS_TIME.format(new Date(fetchedAtIso));
}

// `now` (epoch ms) → het lopende uur als Amsterdam-ISO ("YYYY-MM-DDTHH:00"), zodat het lexicaal
// vergelijkbaar is met de forecast-tijden (die al Amsterdam-lokaal zijn). Voor "vanaf nu"-filtering
// → de view blijft dynamisch, ook als de data (door een rate-limit) een paar uur oud is.
const AMS_HOUR = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Europe/Amsterdam',
});
export function amsterdamHourIso(ms: number): string {
  const parts = AMS_HOUR.formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:00`;
}

// Wandklok-minuten sinds middernacht in Amsterdam (DST-proof) — voor de zon-boog (frac van de dag).
const AMS_HM = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Europe/Amsterdam',
});
export function amsterdamMinutes(ms: number): number {
  const parts = AMS_HM.formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return get('hour') * 60 + get('minute');
}

/** Minuten sinds middernacht uit een lokaal-ISO-tijd ("…T05:25" → 325). */
export function isoMinutes(localIso: string): number {
  const hh = Number(localIso.slice(11, 13));
  const mm = Number(localIso.slice(14, 16));
  return hh * 60 + mm;
}

// Een UTC-instant → Amsterdam-lokale ISO "YYYY-MM-DDTHH:MM" (DST-proof). Voor de nowcast-tijden
// (KNMI levert UTC) zodat ze — net als de forecast-tijden — lokaal zijn: `clock()` werkt direct.
const AMS_LOCAL = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Europe/Amsterdam',
});
export function amsterdamLocalIso(ms: number): string {
  const p = AMS_LOCAL.formatToParts(new Date(ms));
  const get = (type: string) => p.find((x) => x.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

const DOW = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'] as const;
const MONTHS = [
  'jan',
  'feb',
  'mrt',
  'apr',
  'mei',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
] as const;

/** Parse "YYYY-MM-DD" naar een lokale Date zonder tz-verschuiving. */
function parseDate(isoDate: string): Date {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Dag-label voor de 14-daagse: index 0 = vandaag, 1 = morgen, daarna "di" (Open-Meteo daily[0] = vandaag). */
export function dayName(isoDate: string, index: number): string {
  if (index === 0) return 'vandaag';
  if (index === 1) return 'morgen';
  return DOW[parseDate(isoDate).getDay()] ?? '';
}

/** Datum-sublabel "3 jun" (leeg voor vandaag/morgen). */
export function dayDate(isoDate: string, index: number): string {
  if (index < 2) return '';
  const d = parseDate(isoDate);
  return `${d.getDate()} ${MONTHS[d.getMonth()] ?? ''}`;
}

/** Korte numerieke datum "4/6" (dag/maand) — voor de 14-daagse kaarten/rijen (v2-mock). */
export function dayDateNumeric(isoDate: string): string {
  const d = parseDate(isoDate);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

/** Resterende daglicht-tekst "5 u 39 m" uit een aantal minuten (>0). */
export function durationText(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} m`;
  if (r === 0) return `${h} u`;
  return `${h} u ${r} m`;
}

/**
 * "geldig tot"-tijd met dag-prefix wanneer het einde niet vandaag valt: "12:00" (vandaag),
 * "morgen 12:00", of "wo 12:00" (verder vooruit) — voor de waarschuwingsbanner, zodat een
 * waarschuwing die doorloopt tot een andere dag niet als kale "tot 12:00" misleidt. `endMs`/`now`
 * zijn epoch-ms; de dag wordt in Amsterdam-lokale tijd bepaald (DST-proof, net als de forecast-tijden).
 */
export function validUntilClock(endMs: number, now: number): string {
  const endIso = amsterdamLocalIso(endMs);
  const hhmm = clock(endIso);
  const start = parseDate(amsterdamLocalIso(now));
  const end = parseDate(endIso);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (days <= 0) return hhmm;
  if (days === 1) return `morgen ${hhmm}`;
  return `${DOW[end.getDay()] ?? ''} ${hhmm}`;
}
